import React, { useEffect, useRef, useState } from "react";
import { Music, VolumeX } from "lucide-react";
import { toast } from "sonner";

/**
 * MusicToggle - floating pill button that plays a soft romantic tune.
 * Uses HTML5 Audio. If playback fails (autoplay policy / bad URL) it
 * gracefully surfaces a friendly toast.
 */
const DEFAULT_TRACK =
    "https://cdn.pixabay.com/download/audio/2022/11/17/audio_febc508520.mp3";
// Fallback royalty-free track (Pixabay). Replace with your own song by editing
// the `track` prop below or the DEFAULT_TRACK constant above.

export const MusicToggle = ({ track = DEFAULT_TRACK }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const a = new Audio(track);
        a.loop = true;
        a.volume = 0.35;
        a.preload = "auto";
        audioRef.current = a;
        return () => {
            a.pause();
            audioRef.current = null;
        };
    }, [track]);

    const toggle = async () => {
        const a = audioRef.current;
        if (!a) return;
        try {
            if (playing) {
                a.pause();
                setPlaying(false);
            } else {
                await a.play();
                setPlaying(true);
                toast("Playing our little melody 🎶", { duration: 2200 });
            }
        } catch (err) {
            toast("Couldn't play the music. Try tapping again ♡", {
                duration: 2600,
            });
        }
    };

    return (
        <button
            type="button"
            onClick={toggle}
            data-testid="music-toggle-button"
            aria-label={playing ? "Pause music" : "Play music"}
            className="fixed bottom-6 left-6 z-50 flex items-center gap-2 rounded-full bg-white/80 backdrop-blur-md px-5 py-3 border border-dusty text-cocoa shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all font-body text-sm"
        >
            <span
                className={`inline-flex h-2 w-2 rounded-full ${playing ? "bg-dusty-rose animate-pulse" : "bg-cocoa-light"}`}
            />
            {playing ? (
                <VolumeX className="w-4 h-4" />
            ) : (
                <Music className="w-4 h-4" />
            )}
            <span className="tracking-wide">
                {playing ? "Pause our song" : "Play our song"}
            </span>
        </button>
    );
};

export default MusicToggle;
